import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../Icon";
import { COMPANY, WORK_CATEGORIES, WORK_CATEGORY_ICON } from "../data";
import { useWorks } from "../useWorks";
import {
  BOTTOM_CTA,
  CAPABILITY_FLOW,
  CAPABILITY_NOTE,
  CONTRACT_NOTE,
  FOR_YOU,
  FUNDING_BANNER,
  HERO_ACTIVE,
  HERO_HEADLINES,
  HERO_SUB,
  HERO_SUPPORT,
  MESSAGES,
  STAGE_NOTE,
  TECH_SERVICES,
} from "../content";
import {
  ContractTable,
  CtaBand,
  FlowLine,
  MessageBand,
  SectionHead,
  SegmentCards,
  StageStrip,
  useSeo,
} from "../ui";

const Arrow = ({ size = 14 }) => (
  <Icon.ArrowRight width={size} height={size} aria-hidden="true" focusable="false" />
);

/* 1. Hero — 왼쪽 문구 · 오른쪽 작업 사진 */
function Hero() {
  const h = HERO_HEADLINES[HERO_ACTIVE] || HERO_HEADLINES[0];
  return (
    <section id="top" className="hero">
      <div className="hero__topline">
        <div className="kicker">PRODUCT VENTURE PARTNER</div>
        <span className="hero__index">{COMPANY.motto}</span>
      </div>

      <div className="hero__grid">
        <div className="hero__copy">
          <h1 className="hero__title hero__title--partner">
            {h.pre && <>{h.pre}<br /></>}
            <span className="grad">{h.accent}</span>{" "}
            <br className="br-md" />
            {h.post}
          </h1>

          <p className="hero__lead">{HERO_SUB}</p>

          <div className="hero__cta">
            <Link to="/contact" className="btn btn--primary btn--lg">
              개발 문의 하기{" "}
              <Icon.ArrowRight className="arrow-diag" width="18" height="18" aria-hidden="true" focusable="false" />
            </Link>
            <Link to="/process" className="text-link">
              진행 방식 보기 <Arrow size={18} />
            </Link>
          </div>

          <p className="hero__support">
            <span className="hero__cross" aria-hidden="true">+</span> {HERO_SUPPORT}
          </p>
        </div>

        <div className="hero__visual">
          <div className="hero__visual-top" aria-hidden="true">
            <span>{COMPANY.tagline}</span>
            <span>［ I.M ］</span>
          </div>
          <img
            src="/images/hero-prototype-desk.webp"
            alt="회로 설계, PCB 설계, 펌웨어, 소프트웨어 개발 화면과 시제품 회로기판이 놓인 작업 책상"
            width="1672"
            height="941"
            fetchPriority="high"
          />
          <div className="hero__visual-bottom" aria-hidden="true">
            <span><i className="status-dot" /> {COMPANY.motto}</span>
            <span>{COMPANY.name}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* 2. 이런 분을 위한 곳입니다 */
function ForYou() {
  return (
    <section className="section">
      <SectionHead kicker="FOR YOU" title="이런 분을 위한 곳입니다" />
      <ul className="foryou">
        {FOR_YOU.map((f) => {
          const I = Icon[f.icon];
          return (
            <li key={f.title} className="foryou__item">
              <span className="foryou__icon">
                {I ? <I width="20" height="20" aria-hidden="true" focusable="false" /> : null}
              </span>
              <span className="foryou__text">{f.title}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* 3. 이런 분들과 만듭니다 */
function Segments() {
  return (
    <section className="section">
      <SectionHead kicker="WHO WE WORK WITH" title="이런 분들과 만듭니다" />
      <SegmentCards />
    </section>
  );
}

/* 4. I.META가 하는 일 */
function Capability() {
  return (
    <section className="section">
      <SectionHead
        kicker="WHAT WE DO"
        title="I.META가 하는 일"
        desc={MESSAGES.support.oneTeam}
      />
      <FlowLine items={CAPABILITY_FLOW} />
      <p className="flowline__note">{CAPABILITY_NOTE}</p>
      <ul className="roles__list who-chips capability__fields">
        {TECH_SERVICES.map((t) => (
          <li key={t.slug}><Link to={`/services/${t.slug}`}>{t.label}</Link></li>
        ))}
      </ul>
    </section>
  );
}

/* 5. 3가지 진행 방식 요약 */
function Models() {
  return (
    <section className="section">
      <SectionHead
        kicker="HOW WE PARTNER"
        title="3가지 진행 방식"
        desc={`${MESSAGES.support.invest} ${CONTRACT_NOTE}`}
        action={
          <Link to="/partnership" className="btn btn--ghost">
            공동개발 자세히 보기 <Arrow />
          </Link>
        }
      />
      <ContractTable compact />
    </section>
  );
}

/* 6. 진행 단계 요약 */
function Stages() {
  return (
    <div className="band band--ink">
      <section className="section">
        <SectionHead
          kicker="STEP BY STEP"
          title="아이디어 검토 → PoC → MVP → 양산"
          desc={`${MESSAGES.support.proof} ${STAGE_NOTE}`}
          action={
            <Link to="/process" className="btn btn--ghost">
              진행 방식 보기 <Arrow />
            </Link>
          }
        />
        <StageStrip />
      </section>
    </div>
  );
}

function WorkThumb({ work }) {
  const [failed, setFailed] = useState(false);
  const I = Icon[WORK_CATEGORY_ICON[work.category]];
  if (work.images[0] && !failed) {
    return (
      <div className="wcard__thumb">
        <img src={work.images[0]} alt="" loading="lazy" onError={() => setFailed(true)} />
      </div>
    );
  }
  return (
    <div className="wcard__thumb wcard__thumb--empty">
      {I ? <I width="28" height="28" aria-hidden="true" focusable="false" /> : null}
    </div>
  );
}

/* 7. 개발 사례 3~4개 썸네일 */
function WorksPreview() {
  /* 분류마다 첫 사례 1건씩, 남는 자리는 목록 순서대로 채워 4건 */
  const { items: works } = useWorks();
  const firsts = WORK_CATEGORIES
    .map((c) => works.find((w) => w.category === c.id))
    .filter(Boolean);
  const items = [...firsts, ...works.filter((w) => !firsts.includes(w))].slice(0, 4);
  const catLabel = Object.fromEntries(WORK_CATEGORIES.map((c) => [c.id, c.label]));
  if (items.length === 0) return null;
  return (
    <section className="section">
      <SectionHead
        kicker="WORKS"
        title="개발 사례"
        action={
          <Link to="/works" className="btn btn--ghost">
            전체 보기 <Arrow />
          </Link>
        }
      />
      <div className="wgrid wgrid--4">
        {items.map((w) => (
          <Link key={w.id} to={`/works#${w.id}`} className="wcard wcard--link">
            <WorkThumb work={w} />
            <div className="wcard__body">
              <div className="wcard__field">{catLabel[w.category]} · {w.field}</div>
              <h3 className="wcard__title">{w.title}</h3>
              <ul className="scope-tags">
                {w.scopeTags.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* 8. 정부지원사업 배너 */
function FundingBanner() {
  return (
    <section className="section section--tight">
      <Link to="/funding" className="fund-banner">
        <span className="fund-banner__icon">
          <Icon.Award width="22" height="22" aria-hidden="true" focusable="false" />
        </span>
        <span className="fund-banner__text">
          <strong>{FUNDING_BANNER}</strong>
        </span>
        <span className="fund-banner__go">
          정부지원사업 연계 <Arrow />
        </span>
      </Link>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "아이닷메타 I.META | PCB 설계 · 임베디드 펌웨어 · 전자제품 시제품 제작 · 하드웨어 개발 외주",
    description:
      "회로 · PCB 설계, STM32 · ESP32 임베디드 펌웨어, 전자제품 시제품 제작부터 양산까지 한 팀에서 개발하는 하드웨어 개발 외주 파트너 아이닷메타(I.META). 서버 · 웹 · 앱 연동까지 함께 만들고, 가능성 있는 아이디어에는 개발비 일부를 기술투자합니다.",
    path: "/",
    /* 카카오톡 · 문자 링크 미리보기는 검색용 제목 대신 핵심 메시지를 보여 준다 (index.html 의 og 와 같게) */
    ogTitle: `${MESSAGES.main} — I.META`,
    ogDescription: HERO_SUB,
  });

  return (
    <>
      <Hero />
      <ForYou />
      <MessageBand text={MESSAGES.alt.share} />
      <Segments />
      <Capability />
      <MessageBand text={MESSAGES.alt.partner} />
      <Models />
      <Stages />
      <WorksPreview />
      <FundingBanner />
      <CtaBand title={BOTTOM_CTA} desc={MESSAGES.alt.start} />
    </>
  );
}
